/** Durable, transactional wrapped-key vault. Legacy browser data is never deleted. */
import { deleteIndexedDatabase } from './localPurge.js';

const LEGACY_KEY = 'yada:local-wrapped-seeds';
const DATABASE = 'yada-wrapped-seed-vault';
const ROWS = 'seeds';
const META = 'metadata';

export interface LocalWrappedSeed {
  account_id?: string;
  workspace_id: string;
  profile_id: string;
  kid: string;
  wrapped_seed: string;
  wrap_algorithm: string;
  wrap_type?: 'device' | 'recovery';
  device_id?: string;
  created_at: string;
}
export class LocalSeedVaultError extends Error {
  readonly code: 'legacy-corrupt' | 'legacy-changed' | 'invalid-record' | 'conflict' | 'scope-missing' | 'storage-unavailable';
  constructor(code: LocalSeedVaultError['code'], message: string) {
    super(message); this.code = code; this.name = 'LocalSeedVaultError';
  }
}
function validSeed(value: unknown): value is LocalWrappedSeed {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return false;
  const row = value as Record<string, unknown>;
  return ['workspace_id', 'profile_id', 'kid', 'wrapped_seed', 'wrap_algorithm'].every(key => typeof row[key] === 'string' && (row[key] as string).trim().length > 0) &&
    typeof row.created_at === 'string' && (row.account_id === undefined || typeof row.account_id === 'string' && row.account_id.trim().length > 0) &&
    (row.device_id === undefined || typeof row.device_id === 'string') &&
    (row.wrap_type === undefined || row.wrap_type === 'device' || row.wrap_type === 'recovery') &&
    /^[A-Za-z0-9+/]+={0,2}$/.test(row.wrapped_seed as string);
}
function identity(row: LocalWrappedSeed, account = true): string {
  return JSON.stringify([account ? row.account_id ?? null : null, row.profile_id, row.workspace_id, row.kid, row.device_id || null, row.wrap_type ?? 'device']);
}
function sameEnvelope(a: LocalWrappedSeed, b: LocalWrappedSeed) {
  return a.wrapped_seed === b.wrapped_seed && a.wrap_algorithm === b.wrap_algorithm;
}
export function parseLegacyWrappedSeeds(raw: string | null): LocalWrappedSeed[] {
  if (raw === null) return [];
  let rows: unknown;
  try { rows = JSON.parse(raw); } catch { throw new LocalSeedVaultError('legacy-corrupt', 'Legacy encryption keys contain invalid JSON. The original browser data has been preserved.'); }
  if (!Array.isArray(rows) || !rows.every(validSeed)) throw new LocalSeedVaultError('legacy-corrupt', 'Legacy encryption keys contain invalid records. No keys were imported or replaced; the original data has been preserved.');
  const known = new Map<string, LocalWrappedSeed>();
  for (const row of rows) {
    const previous = known.get(identity(row));
    if (previous && !sameEnvelope(previous, row)) throw new LocalSeedVaultError('legacy-corrupt', 'Legacy encryption keys contain conflicting copies. The original data has been preserved.');
    known.set(identity(row), row);
  }
  return [...known.values()].map(row => ({ ...row, wrap_type: row.wrap_type ?? 'device' }));
}
function openVault(): Promise<IDBDatabase> {
  if (typeof indexedDB === 'undefined') return Promise.reject(new LocalSeedVaultError('storage-unavailable', 'IndexedDB is unavailable. Encryption keys have not been changed.'));
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DATABASE, 1);
    request.onupgradeneeded = () => {
      request.result.createObjectStore(ROWS);
      request.result.createObjectStore(META);
    };
    let blocked = false;
    request.onsuccess = () => {
      const db = request.result;
      if (blocked) { db.close(); return; }
      db.onversionchange = () => db.close();
      resolve(db);
    };
    request.onerror = () => reject(new LocalSeedVaultError('storage-unavailable', 'Could not open the encryption key vault. Existing keys have been preserved.'));
    request.onblocked = () => { blocked = true; reject(new LocalSeedVaultError('storage-unavailable', 'Another browser tab is blocking the key vault. Close it and retry without clearing browser data.')); };
  });
}
function legacySnapshot() {
  let raw: string | null;
  try { raw = localStorage.getItem(LEGACY_KEY); } catch { throw new LocalSeedVaultError('storage-unavailable', 'Legacy key storage could not be read. Existing keys have been preserved.'); }
  return { raw, rows: parseLegacyWrappedSeeds(raw) };
}
/** Every operation checks legacy integrity; migration itself commits exactly once. */
async function vaultOperation<T>(run: (store: IDBObjectStore, succeed: (result: T) => void, fail: (error: Error) => void) => void): Promise<T> {
  const legacy = legacySnapshot(); // Validate the WHOLE snapshot before opening a write transaction.
  const db = await openVault();
  return new Promise<T>((resolve, reject) => {
    let transaction: IDBTransaction;
    try { transaction = db.transaction([ROWS, META], 'readwrite', { durability: 'strict' }); } catch (error) {
      if (error instanceof TypeError) {
        try { transaction = db.transaction([ROWS, META], 'readwrite'); } catch { db.close(); reject(new LocalSeedVaultError('storage-unavailable', 'Key vault transactions are unavailable. Existing keys have been preserved.')); return; }
      } else {
        db.close(); reject(new LocalSeedVaultError('storage-unavailable', 'Key vault transactions are unavailable. Existing keys have been preserved.')); return;
      }
    }
    const store = transaction.objectStore(ROWS);
    const metadata = transaction.objectStore(META);
    let failure: Error | undefined;
    let result: T;
    const fail = (error: Error) => { failure = error; transaction.abort(); };
    const succeed = (value: T) => { result = value; };
    transaction.oncomplete = () => { db.close(); resolve(result); };
    transaction.onerror = () => { /* onabort handles request errors */ };
    transaction.onabort = () => { db.close(); reject(failure ?? new LocalSeedVaultError('storage-unavailable', 'Key vault transaction failed. Existing keys have been preserved.')); };
    const marker = metadata.get('legacy-import');
    marker.onsuccess = () => {
      try {
        const current = legacySnapshot();
        if (current.raw !== legacy.raw) return fail(new LocalSeedVaultError('legacy-changed', 'Legacy keys changed while opening the vault. Original data has been preserved; retry without clearing storage.'));
      } catch (error) { return fail(error as Error); }
      if (marker.result !== undefined) {
        if (marker.result.raw !== legacy.raw) return fail(new LocalSeedVaultError('legacy-changed', 'Legacy encryption keys changed after migration. Both copies have been preserved; resolve them before continuing.'));
        run(store, succeed, fail); return;
      }
      const existing = store.getAll();
      existing.onsuccess = () => {
        const records = existing.result as LocalWrappedSeed[];
        if (!records.every(validSeed)) return fail(new LocalSeedVaultError('invalid-record', 'The encryption key vault contains an invalid record. Existing data has been preserved.'));
        for (const row of legacy.rows) {
          const prior = records.find(copy => identity(copy) === identity(row));
          if (prior && !sameEnvelope(prior, row)) return fail(new LocalSeedVaultError('conflict', 'Legacy migration conflicts with an existing encryption key. No copies were overwritten.'));
          if (!prior) store.add(row, identity(row));
        }
        metadata.add({ raw: legacy.raw }, 'legacy-import');
        run(store, succeed, fail);
      };
    };
  });
}
export async function getLocalWrappedSeeds(workspaceId?: string, filters?: { account_id?: string; device_id?: string; wrap_type?: string }): Promise<LocalWrappedSeed[]> {
  return vaultOperation((store, succeed, fail) => {
    const request = store.getAll();
    request.onsuccess = () => {
      const rows = request.result as LocalWrappedSeed[];
      if (!rows.every(validSeed)) return fail(new LocalSeedVaultError('invalid-record', 'The encryption key vault contains invalid records. Existing data has been preserved.'));
      succeed(rows.filter(row => (!workspaceId || row.workspace_id === workspaceId) &&
        (filters?.account_id === undefined || row.account_id === filters.account_id) &&
        (filters?.device_id === undefined || row.device_id === filters.device_id || Boolean(workspaceId && row.account_id === undefined && !row.device_id)) &&
        (!filters?.wrap_type || (row.wrap_type ?? 'device') === filters.wrap_type)));
    };
  });
}
export async function storeLocalWrappedSeed(seed: LocalWrappedSeed): Promise<LocalWrappedSeed> {
  if (!validSeed(seed)) throw new LocalSeedVaultError('invalid-record', 'Refusing an invalid encryption-key record. Existing keys have been preserved.');
  if ((seed.wrap_type ?? 'device') === 'device' && !seed.device_id?.trim()) throw new LocalSeedVaultError('invalid-record', 'A device identity is required for new device-key copies. Existing keys have been preserved.');
  let accountId = seed.account_id;
  if (!accountId) {
    try { accountId = JSON.parse(localStorage.getItem('yada:account:account') || 'null')?.id; } catch { /* Unavailable identity cannot authorize a new write. */ }
  }
  if (typeof accountId !== 'string' || !accountId) throw new LocalSeedVaultError('scope-missing', 'An account identity is required before saving encryption keys. Existing keys have been preserved.');
  const incoming = { ...seed, account_id: accountId, wrap_type: seed.wrap_type ?? 'device' };
  return vaultOperation((store, succeed, fail) => {
    const request = store.getAll();
    request.onsuccess = () => {
      const rows = request.result as LocalWrappedSeed[];
      if (!rows.every(validSeed)) return fail(new LocalSeedVaultError('invalid-record', 'The encryption key vault contains invalid records. Existing data has been preserved.'));
      const copies = rows.filter(row => identity(row) === identity(incoming) || row.account_id === undefined &&
        row.profile_id === incoming.profile_id && row.workspace_id === incoming.workspace_id && row.kid === incoming.kid &&
        (row.wrap_type ?? 'device') === incoming.wrap_type && (!row.device_id || row.device_id === incoming.device_id));
      if (copies.some(row => !sameEnvelope(row, incoming))) return fail(new LocalSeedVaultError('conflict', 'This encryption-key identity already has a different wrapped copy. Verify and reuse the original copy; no key was replaced.'));
      const previous = copies.find(row => identity(row) === identity(incoming)) ?? copies[0];
      if (previous) { succeed(previous); return; }
      store.add(incoming, identity(incoming)); succeed(incoming);
    };
  });
}

/**
 * Reset local wrapped seed vault database entirely (used when purging local data).
 */
export async function clearLocalWrappedSeedVault(): Promise<void> {
  await deleteIndexedDatabase(DATABASE);
}
